import { useEffect, useMemo, useState } from "react";
import {
    Button,
    Input,
    Table,
    Row,
    Col,
    FormText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Label,
} from "reactstrap";
import { useTheme } from '../../../hooks/useTheme';
import CopyToClipboardButton from '../../../components/CopyToClipboardButton';
import RichTextEditor from "../../../components/RichTextEditor/RichTextEditor";
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { fetchSalesforceFields } from '../../opportunities/data/opportunitiesService';
import { useQuery } from '@tanstack/react-query';
import TestDragAndDrop from "../../../components/TestDragAndDrop";

// Drop-in component for managing Quote Template custom fields.
// Table is read-only (shows only the generated field name) with a Delete action.
// "Add field" opens a modal where the user sets label/type and config; the table only lists the field name.
// Handles `data` being undefined/non-array by showing "No Custom Fields".
// Mutates incoming `data` array in place so upstream JSON.stringify diffing still works.
//
// NEW in this version:
// - safeConfig normalization (config can be array or JSON string)
// - config sortOrder drives group ordering
// - row.sortOrder drives item ordering within a group
// - expanded/collapsed is stored in local UI state
// - drag/drop persists changes by updating config + rows then calling commit()

const TYPE_TEXTAREA = "TextArea";
const TYPE_LOOKUP = "Lookup";
const TYPE_TEXT = "Text"; // NEW: single-line plain text
const TYPE_REGION = "Region";

const genId = () => `cf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const toFieldKey = (label, type) => {
    const base = (label || "").toString();
    if (!base) return "";
    const upper = base
        .normalize("NFKD")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .replace(/_+/g, "_")
        .toUpperCase();
    return type === 'Region' ? `CUSTOM_REGION_${upper}` : `CUSTOM_${upper}`;
};

const normalizeField = (f) => ({
    id: f?.id ?? genId(),
    label: f?.label ?? f?.name ?? "",
    field: f?.field ?? toFieldKey(f?.label ?? f?.name ?? "", f?.type ?? ""),
    // Preserve existing values; default to TextArea unless explicitly Lookup or Text
    type: f?.type === TYPE_LOOKUP ? TYPE_LOOKUP : (f?.type === TYPE_TEXT ? TYPE_TEXT : f?.type === TYPE_TEXTAREA ? TYPE_TEXTAREA : TYPE_REGION),
    section: f?.section ?? "",
    // NEW: per-section sort order for items
    sortOrder: typeof f?.sortOrder === "number" ? f.sortOrder : 0,
    defaultValue: f?.defaultValue ?? "",
    salesforceDefaultValue: f?.salesforceDefaultValue ?? "",
    options: Array.isArray(f?.options)
        ? f.options.map((o) => ({ id: o?.id ?? genId(), label: o?.label ?? "", value: o?.value ?? "" }))
        : [],
});

const sectionKey = (s) => (s || "").trim() || "Section 1";

const moveInArray = (arr, from, to) => {
    const copy = [...arr];
    const [x] = copy.splice(from, 1);
    copy.splice(to, 0, x);
    return copy;
};

export default function AdminQuoteTemplateCustomSectionsTable({ data, onChange, config }) {
    const theme = useTheme();

    // Local copy for rendering
    const initialRows = useMemo(() => {
        try {
            return typeof (data) === 'string'
                ? (Array.isArray(JSON.parse(data)) ? JSON.parse(data).map(normalizeField) : [])
                : (Array.isArray(data) ? data.map(normalizeField) : []);
        } catch {
            return [];
        }
    }, [data]);

    const [rows, setRows] = useState(initialRows);

    const safeConfig = useMemo(() => {
        if (!config) return [];
        if (Array.isArray(config)) return config;

        // common case: config comes in as JSON string
        if (typeof config === "string") {
            try {
                const parsed = JSON.parse(config);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }

        // anything else (object, number, etc.)
        return [];
    }, [config]);

    // Always work with a sorted version of config for display + flattening
    const orderedConfig = useMemo(() => {
        const s1 = "Section 1";

        return [...safeConfig].sort((a, b) => {
            const an = sectionKey(a?.name);
            const bn = sectionKey(b?.name);

            // pin Section 1 first
            if (an === s1 && bn !== s1) return -1;
            if (bn === s1 && an !== s1) return 1;

            // then by sortOrder
            const ao = typeof a?.sortOrder === "number" ? a.sortOrder : 0;
            const bo = typeof b?.sortOrder === "number" ? b.sortOrder : 0;
            if (ao !== bo) return ao - bo;

            // stable fallback
            return (a?.name || "").localeCompare(b?.name || "");
        });
    }, [safeConfig]);


    // UI-only expanded/collapsed state
    const [expandedBySectionId, setExpandedBySectionId] = useState(() => ({}));

    // Modal state (used for both Add and Edit)
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editIndex, setEditIndex] = useState(null); // null => Add mode, number => Edit mode
    const [newField, setNewField] = useState({
        label: "",
        field: "",
        type: TYPE_TEXTAREA,
        section: "",
        defaultValue: "",
        salesforceDefaultValue: "",
        options: []
    });

    const commit = (nextRows, nextConfig) => {
        setRows(nextRows);
        if (Array.isArray(data)) {
            data.splice(0, data.length, ...nextRows);
        }
        onChange?.(nextRows, nextConfig);
    };

    const openAdd = () => {
        setNewField({ label: "", field: "", section: "", type: TYPE_TEXTAREA, defaultValue: "", salesforceDefaultValue: "", options: [] });
        setEditIndex(null);
        setIsAddOpen(true);
    };

    const closeAdd = () => {
        setIsAddOpen(false);
        setEditIndex(null);
    };

    useEffect(() => {
        setNewField((s) => ({ ...s, field: toFieldKey(s.label, s.type) }));
    }, [newField.type]);

    // ---------- helpers for ordering + config maintenance ----------
    const ensureSectionExists = (sectionName, cfg) => {
        const sec = sectionKey(sectionName);
        if (!sec) return cfg;

        const exists = (cfg || []).some((s) => sectionKey(s?.name) === sec);
        if (exists) return cfg;

        return [...(cfg || []), { name: sec, sortOrder: (cfg?.length || 0) + 1 }];
    };

    const removeUnusedSectionsAndNormalize = (nextRows, cfg) => {
        const usedSections = new Set(
            (nextRows || [])
                .map((r) => sectionKey(r.section))
                .filter(Boolean)
        );

        let nextConfig = (cfg || []).filter((s) => usedSections.has(sectionKey(s?.name)));

        nextConfig = nextConfig
            .sort((a, b) => {
                const ao = typeof a?.sortOrder === "number" ? a.sortOrder : 0;
                const bo = typeof b?.sortOrder === "number" ? b.sortOrder : 0;
                if (ao !== bo) return ao - bo;
                return (a?.name || "").localeCompare(b?.name || "");
            })
            .map((s, idx) => ({ ...s, sortOrder: idx + 1 }));

        return nextConfig;
    };

    const renumberItemSortOrdersInSection = (allRows, sectionName) => {
        const sec = sectionKey(sectionName);

        const inSec = (allRows || [])
            .filter((r) => sectionKey(r.section) === sec)
            .slice()
            .sort((a, b) => {
                const ao = typeof a?.sortOrder === "number" ? a.sortOrder : 0;
                const bo = typeof b?.sortOrder === "number" ? b.sortOrder : 0;
                if (ao !== bo) return ao - bo;
                return (a?.field || "").localeCompare(b?.field || "");
            });

        const idToOrder = new Map();
        inSec.forEach((r, idx) => idToOrder.set(r.id, idx + 1));

        return (allRows || []).map((r) => {
            if (sectionKey(r.section) !== sec) return r;
            return { ...r, sortOrder: idToOrder.get(r.id) ?? 0 };
        });
    };

    const renumberBothSectionsAfterMove = (allRows, fromSection, toSection) => {
        let updated = allRows;
        if (fromSection) updated = renumberItemSortOrdersInSection(updated, fromSection);
        if (toSection && toSection !== fromSection) updated = renumberItemSortOrdersInSection(updated, toSection);
        return updated;
    };
    // -------------------------------------------------------------

    const saveFieldFromModal = () => {
        const label = (newField.label || "").trim();
        if (!label) return;

        const fieldKey = toFieldKey(label, newField.type);
        const sectionName = sectionKey(newField.section);

        // Determine item's sortOrder: if adding, append to end of section
        const inSection = (rows || []).filter(r => sectionKey(r.section) === sectionName);
        const maxSort = Math.max(0, ...inSection.map(r => (typeof r.sortOrder === "number" ? r.sortOrder : 0)));
        const nextSortOrder = editIndex != null
            ? (typeof rows[editIndex]?.sortOrder === "number" ? rows[editIndex].sortOrder : maxSort + 1)
            : maxSort + 1;

        // Build normalized field from modal draft
        const normalized = normalizeField({
            id: editIndex != null ? rows[editIndex]?.id : genId(),
            label,
            field: fieldKey,
            section: sectionName,
            type:
                newField.type === TYPE_LOOKUP
                    ? TYPE_LOOKUP
                    : newField.type === TYPE_TEXT
                        ? TYPE_TEXT
                        : newField.type === TYPE_TEXTAREA
                            ? TYPE_TEXTAREA : TYPE_REGION,
            sortOrder: nextSortOrder,
            defaultValue: newField.type === TYPE_LOOKUP ? "" : (newField.defaultValue || ""),
            salesforceDefaultValue: newField.salesforceDefaultValue,
            options:
                newField.type === TYPE_LOOKUP
                    ? (newField.options || [])
                        .filter((o) => (o.label || "").trim() || (o.value || "").trim())
                        .map((o) => ({
                            id: o?.id ?? genId(),
                            label: o.label || "",
                            value: o.value || "",
                        }))
                    : [],
        });

        // IMPORTANT: build from safeConfig, not raw `config` (which might be a string)
        let newConfig = ensureSectionExists(sectionName, [...orderedConfig]);

        // Build nextRows with the new/edited field applied
        let nextRows = [];
        if (editIndex != null) {
            nextRows = (rows || []).map((r, i) => (i === editIndex ? normalized : r));
        } else {
            nextRows = [...(rows || []), normalized];
        }

        // Normalize item sortOrder for that section
        nextRows = renumberItemSortOrdersInSection(nextRows, sectionName);

        // Remove redundant sections from config (no rows reference them) and normalize sortOrder
        newConfig = removeUnusedSectionsAndNormalize(nextRows, newConfig);

        commit(nextRows, newConfig);
        closeAdd();
    };

    // === id-based edit & delete ===
    const openEditById = (id) => {
        const idx = rows.findIndex(r => r.id === id);
        if (idx === -1) return;
        const f = rows[idx];
        setNewField({
            label: f.label || "",
            field: f.field || toFieldKey(f.label || "", f.type || ""),
            type: f.type === TYPE_LOOKUP ? TYPE_LOOKUP : (f.type === TYPE_TEXT ? TYPE_TEXT : f.type === TYPE_TEXTAREA ? TYPE_TEXTAREA : TYPE_REGION),
            section: f.section,
            defaultValue: f.defaultValue || "",
            salesforceDefaultValue: f.salesforceDefaultValue || "",
            options: Array.isArray(f.options)
                ? f.options.map(o => ({ id: o?.id ?? genId(), label: o?.label || "", value: o?.value || "" }))
                : [],
        });
        setEditIndex(idx);
        setIsAddOpen(true);
    };

    const removeFieldById = (id) => {
        let nextRows = (rows || []).filter(r => r.id !== id);

        // IMPORTANT: start from orderedConfig, not raw config
        let newConfig = removeUnusedSectionsAndNormalize(nextRows, [...orderedConfig]);

        // Also renumber item sortOrder for all sections (cheap enough at this scale)
        const usedSections = [...new Set(nextRows.map(r => sectionKey(r.section)))];
        usedSections.forEach(sec => { nextRows = renumberItemSortOrdersInSection(nextRows, sec); });

        commit(nextRows, newConfig);
    };
    // ===================================

    // Read-only columns for DataTable: ONLY the generated field name + delete action
    const columns = [
        {
            key: "field",
            label: "Field",
            groupHeader: true,
            groupColumn: true,
            render: (cell, row) => (
                row.topLevelRow
                    ? <span>{row.section}</span>
                    : <div className="d-flex gap-2 align-items-center">
                        <span onClick={(e) => e.stopPropagation()}>
                            <CopyToClipboardButton text={row.field || toFieldKey(row.label, row.type) || "—"} />
                        </span>
                        {row.field}
                    </div>
            ),
        },
        {
            key: "actions",
            label: "",
            align: "right",
            groupColumn: true,
            render: (cell, row) => (
                row.rowType === 'group'
                    ? undefined
                    : <div className="d-flex" style={{ justifyContent: "flex-end" }}>
                        <Button
                            color="danger"
                            size="sm"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeFieldById(row.id);
                            }}
                            title="Delete field"
                        >
                            <i className="bi bi-trash" />
                        </Button>
                    </div>
            ),
        },
    ];

    const isBadInput = !Array.isArray(data) && data && typeof data === "object";
    const isEmpty = rows.length === 0;

    const sectionSuggestions = useMemo(() => {
        const unique = [...new Set(rows.map(r => sectionKey(r.section)).filter(Boolean))];
        return unique.map(section => ({ value: section, label: section }));
    }, [rows]);

    const selectedSectionOption = useMemo(() => {
        const s = (newField.section || "").trim();
        if (!s) return null;

        const match = sectionSuggestions.find(o => o.value === s);
        return match ?? { value: s, label: s };
    }, [newField.section, sectionSuggestions]);

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    const selectStyle = {
        control: (base, state) => ({
            ...base,
            minHeight: '32px',
            height: '32px',
            fontSize: '0.875rem',
            borderRadius: '0.375rem',
            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#3b82f6',
            },
        }),
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    };

    // Build the flattened list *in group order* with children directly after each group.
    // This matches your TestDragAndDrop depth-based parent logic.
    const nestedRows = useMemo(() => {
        // Group rows by section
        const rowsBySection = new Map();
        (rows || []).forEach(r => {
            const sec = sectionKey(r.section);
            if (!rowsBySection.has(sec)) rowsBySection.set(sec, []);
            rowsBySection.get(sec).push(r);
        });

        const flattened = [];

        // Render groups in config order (sorted), with children immediately after
        orderedConfig.forEach((section) => {
            const sec = sectionKey(section?.name);
            const groupId = `section-${sec}`;
            const expanded = expandedBySectionId[groupId] ?? true;

            flattened.push({
                id: groupId,
                field: sec,
                section: sec,
                rowType: "group",
                depth: 0,
                expanded,
                visible: true,
                showChildren: true
            });

            (rowsBySection.get(sec) || [])
                .slice()
                .sort((a, b) => {
                    const ao = typeof a?.sortOrder === "number" ? a.sortOrder : 0;
                    const bo = typeof b?.sortOrder === "number" ? b.sortOrder : 0;
                    if (ao !== bo) return ao - bo;
                    return (a?.field || "").localeCompare(b?.field || "");
                })
                .forEach((item) => {
                    flattened.push({
                        id: item.id,
                        field: item.field,
                        section: sec,
                        rowType: "child",
                        depth: 1,
                        visible: true,
                        groupIsVisible: true
                    });
                });
        });

        // Sections that exist in rows but not config: append them after config groups
        for (const [sec, items] of rowsBySection.entries()) {
            if (orderedConfig.some(c => sectionKey(c?.name) === sec)) continue;

            const groupId = `section-${sec}`;
            const expanded = expandedBySectionId[groupId] ?? true;

            flattened.push({
                id: groupId,
                field: sec,
                section: sec,
                rowType: "group",
                depth: 0,
                expanded,
                visible: true,
                showChildren: true,
            });

            items
                .slice()
                .sort((a, b) => {
                    const ao = typeof a?.sortOrder === "number" ? a.sortOrder : 0;
                    const bo = typeof b?.sortOrder === "number" ? b.sortOrder : 0;
                    if (ao !== bo) return ao - bo;
                    return (a?.field || "").localeCompare(b?.field || "");
                })
                .forEach(item => {
                    flattened.push({
                        id: item.id,
                        field: item.field,
                        section: sec,
                        rowType: "child",
                        depth: 1,
                        visible: true,
                        groupIsVisible: true,
                    });
                });
        }

        return flattened;
    }, [rows, orderedConfig, expandedBySectionId]);

    // --------- drag/drop persistence handlers ----------
    const onGroupMoved = (groupId, newPosition) => {
        const s1 = "Section 1";
        const movedSectionName = sectionKey(groupId.replace(/^section-/, ""));

        const oldIndex = orderedConfig.findIndex(s => sectionKey(s?.name) === movedSectionName);
        if (oldIndex === -1) return;

        // find where Section 1 currently is in orderedConfig (should be 0, but be safe)
        const s1Index = orderedConfig.findIndex(s => sectionKey(s?.name) === s1);

        // clamp so nothing can be inserted above Section 1
        let targetIndex = newPosition;
        if (movedSectionName !== s1) {
            targetIndex = Math.max(targetIndex, s1Index + 1);
        }

        const moved = moveInArray(orderedConfig, oldIndex, targetIndex).map((s, idx) => {
            // keep whatever existing props, but normalize sortOrder
            return { ...s, sortOrder: idx + 1 };
        });

        const trimmed = removeUnusedSectionsAndNormalize(rows, moved);
        commit(rows, trimmed);
    };


    const onItemMoved = ({ itemId, fromGroupId, toGroupId, newPosition }) => {
        const fromSection = sectionKey(fromGroupId?.replace(/^section-/, ""));
        const toSection = sectionKey(toGroupId?.replace(/^section-/, ""));

        if (!toSection) return;

        // 1) Move the item's section (even if it stays in same section, this is harmless)
        let nextRows = (rows || []).map(r => (r.id === itemId ? { ...r, section: toSection } : r));

        // 2) Rebuild the target section ordering using sortOrder, then insert at newPosition
        const targetItems = nextRows
            .filter(r => sectionKey(r.section) === toSection)
            .slice()
            .sort((a, b) => {
                const ao = typeof a?.sortOrder === "number" ? a.sortOrder : 0;
                const bo = typeof b?.sortOrder === "number" ? b.sortOrder : 0;
                if (ao !== bo) return ao - bo;
                return (a?.field || "").localeCompare(b?.field || "");
            });

        const movedItem = targetItems.find(r => r.id === itemId);
        const others = targetItems.filter(r => r.id !== itemId);
        if (!movedItem) return;

        // clamp newPosition
        const insertAt = Math.max(0, Math.min(newPosition ?? 0, others.length));
        others.splice(insertAt, 0, movedItem);

        // assign 1..n sortOrder in target section
        const idToOrder = new Map();
        others.forEach((r, idx) => idToOrder.set(r.id, idx + 1));

        nextRows = nextRows.map(r => {
            if (sectionKey(r.section) !== toSection) return r;
            return { ...r, sortOrder: idToOrder.get(r.id) ?? (r.sortOrder ?? 0) };
        });

        // 3) Renumber source section too (if moved across)
        nextRows = renumberBothSectionsAfterMove(nextRows, fromSection, toSection);

        // 4) Ensure config contains toSection, remove unused, normalize group sortOrder
        let nextConfig = ensureSectionExists(toSection, [...orderedConfig]);
        nextConfig = removeUnusedSectionsAndNormalize(nextRows, nextConfig);

        commit(nextRows, nextConfig);
    };

    const onGroupExpandedChanged = (id, expanded) => {
        setExpandedBySectionId(prev => ({ ...prev, [id]: expanded }));
    };
    // --------------------------------------------------

    const {
        data: opportunityFieldNames
    } = useQuery({
        queryKey: ["opportunityFieldNames"],
        queryFn: () => fetchSalesforceFields("Opportunities"),
        staleTime: 60000
    });

    const {
        data: accountFieldNames
    } = useQuery({
        queryKey: ["accountFieldNames"],
        queryFn: () => fetchSalesforceFields("Accounts"),
        staleTime: 60000
    });

    const salesforceDefaultValues = useMemo(() => {
        const opp = Array.isArray(opportunityFieldNames) ? opportunityFieldNames.map(x => ({ ...x, name: "opportunity_" + x.name, label: "Opp: " + x.label })) : [];
        const acct = Array.isArray(accountFieldNames) ? accountFieldNames.map(x => ({ ...x, name: "account_" + x.name, label: "Acct: " + x.label })) : [];

        return [...opp, ...acct]
            .sort((a, b) => (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" }))
            .map(f => ({
                value: f.name,
                label: f.label,
            }));
    }, [opportunityFieldNames, accountFieldNames]);

    const selectedSalesforceDefaultValue = useMemo(() => {
        return salesforceDefaultValues.find(f => f.value === newField.salesforceDefaultValue) ?? null;
    }, [salesforceDefaultValues, newField.salesforceDefaultValue]);

    return (
        <div>
            <Row className="mb-2">
                <Col className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Custom Fields</h6>
                    <div>
                        <Button size="sm" onClick={openAdd}>Add field</Button>
                    </div>
                </Col>
            </Row>

            {(isEmpty || isBadInput) && <div className="text-muted mb-3">No Custom Fields</div>}

            {!isEmpty && (
                <TestDragAndDrop
                    items={nestedRows}
                    columns={columns}
                    onGroupMoved={onGroupMoved}
                    onItemMoved={onItemMoved}
                    onGroupExpandedChanged={onGroupExpandedChanged}
                    allowChildPress
                    onRowClick={(row) => {
                        // Only allow editing for real items (children)
                        if (!row || row.rowType === "group") return;
                        openEditById(row.id);
                    }}
                />
            )}

            <FormText>
                Tip: fields are generated from the label (e.g., "Terms and Conditions" → CUSTOM_TERMS_AND_CONDITIONS).
            </FormText>

            {/* Add/Edit Field Modal */}
            <Modal isOpen={isAddOpen} toggle={closeAdd} size="lg" centered scrollable>
                <ModalHeader toggle={closeAdd}>{editIndex != null ? "Edit Custom Field" : "Add Custom Field"}</ModalHeader>
                <ModalBody scrollable>
                    <Row className="d-flex align-items-center">
                        <Col md={6}>
                            <FormGroup>
                                <Label>Label</Label>
                                <Input
                                    type="text"
                                    value={newField.label}
                                    placeholder="e.g. Assumptions"
                                    onChange={(e) => {
                                        const label = e.target.value;
                                        setNewField((s) => ({ ...s, label, field: toFieldKey(label, newField.type) }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Field (auto-generated)</Label>
                                <Input type="text" value={newField.field || toFieldKey(newField.label, newField.type)} readOnly />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Type</Label>
                                <Input
                                    type="select"
                                    value={newField.type}
                                    onChange={(e) => setNewField((s) => ({ ...s, type: e.target.value, defaultValue: e.target.value === TYPE_REGION ? true : s.defaultValue }))}
                                >
                                    <option>{TYPE_TEXTAREA}</option>
                                    <option>{TYPE_TEXT}</option>
                                    <option>{TYPE_LOOKUP}</option>
                                    <option>{TYPE_REGION}</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Section</Label>
                                <CreatableSelect
                                    isClearable
                                    options={sectionSuggestions}
                                    onChange={(opt) => {
                                        setNewField((s) => ({ ...s, section: opt ? opt.value : "" }));
                                    }}
                                    value={selectedSectionOption}
                                    classNamePrefix="custom-select data-select"
                                    theme={customTheme}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        ...selectStyle,
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        menu: (base) => ({ ...base, zIndex: 9999 }),
                                    }}
                                    placeholder="Type or select a section..."
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {newField.type === TYPE_TEXTAREA && (
                        <FormGroup>
                            <Label>Default value (optional)</Label>
                            <RichTextEditor
                                value={newField.defaultValue}
                                onChange={(html) => setNewField((s) => ({ ...s, defaultValue: html }))}
                                placeholder="Default text inserted into the document when this field is used"
                                minHeight={200}
                                readOnly={newField.salesforceDefaultValue !== ""}
                            />
                        </FormGroup>
                    )}

                    {newField.type === TYPE_TEXT && (
                        <FormGroup>
                            <Label>Default value (optional)</Label>
                            <Input
                                type="text"
                                value={newField.defaultValue}
                                onChange={(e) => setNewField((s) => ({ ...s, defaultValue: e.target.value }))}
                                placeholder="Default single-line text"
                                disabled={newField.salesforceDefaultValue !== ""}
                            />
                        </FormGroup>
                    )}

                    {newField.type === TYPE_LOOKUP && (
                        <div>
                            <Label>Lookup options</Label>
                            <Table bordered responsive className="mb-2">
                                <thead>
                                    <tr>
                                        <th style={{ width: "22%" }}>Label</th>
                                        <th>Value (textarea)</th>
                                        <th style={{ width: 1 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(newField.options || []).map((opt, idx) => (
                                        <tr key={opt.id ?? idx}>
                                            <td>
                                                <Input
                                                    type="text"
                                                    value={opt.label || ""}
                                                    placeholder="Option label"
                                                    onChange={(e) => {
                                                        const next = [...(newField.options || [])];
                                                        next[idx] = { ...next[idx], label: e.target.value };
                                                        setNewField((s) => ({ ...s, options: next }));
                                                    }}
                                                    disabled={newField.salesforceDefaultValue !== ""}
                                                />
                                            </td>
                                            <td>
                                                <Input
                                                    type="textarea"
                                                    rows={4}
                                                    value={opt.value || ""}
                                                    onChange={(e) => {
                                                        const next = [...(newField.options || [])];
                                                        next[idx] = { ...next[idx], value: e.target.value };
                                                        setNewField((s) => ({ ...s, options: next }));
                                                    }}
                                                    disabled={newField.salesforceDefaultValue !== ""}
                                                    placeholder="Long text for this option"
                                                />
                                            </td>
                                            <td className="text-nowrap">
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    onClick={() => {
                                                        const next = (newField.options || []).filter((_, i) => i !== idx);
                                                        setNewField((s) => ({ ...s, options: next }));
                                                    }}
                                                    disabled={newField.salesforceDefaultValue !== ""}
                                                >
                                                    <i className="bi bi-trash" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {(!newField.options || newField.options.length === 0) && (
                                        <tr>
                                            <td colSpan={3} className="text-muted text-center">
                                                No options yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                            <Button
                                size="sm"
                                onClick={() =>
                                    setNewField((s) => ({
                                        ...s,
                                        options: [...(s.options || []), { id: genId(), label: "", value: "" }],
                                    }))
                                }
                                disabled={newField.salesforceDefaultValue !== ""}
                            >
                                Add option
                            </Button>
                        </div>
                    )}

                    {newField.type !== TYPE_REGION &&
                        <FormGroup>
                            <Label>Salesforce default value (optional)</Label>
                            <Select
                                isClearable
                                isDisabled={newField.defaultValue !== ""}
                                options={salesforceDefaultValues}
                                onChange={(opt) => setNewField((s) => ({ ...s, salesforceDefaultValue: opt ? opt.value : "" }))}
                                value={selectedSalesforceDefaultValue}
                                classNamePrefix="custom-select data-select"
                                theme={customTheme}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    ...selectStyle,
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                placeholder="Select an option..."
                            />
                        </FormGroup>
                    }
                </ModalBody>
                <ModalFooter>
                    <Button color={theme === "dark-mode" ? "dark" : "light"} outline onClick={closeAdd}>
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        onClick={saveFieldFromModal}
                        disabled={!newField.label.trim()}
                    >
                        {editIndex != null ? "Update" : "Save"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
